/**
 * htmlSection.ts — Bloco "Seção em HTML livre" (`section.html`)
 *
 * Uma página inteira escrita em HTML (normalmente gerada por IA) entra no
 * Studio fatiada em vários blocos deste tipo. Assim o conteúdo continua sendo
 * um documento de blocos comum: o usuário move, duplica, esconde e apaga cada
 * faixa, e edita os textos, links e imagens pelo painel de propriedades — sem
 * precisar mexer em código.
 *
 * Regras:
 *  - o HTML e o CSS ficam em propriedades AVANÇADAS (fora do caminho de quem
 *    só quer trocar o conteúdo) e são sempre saneados no servidor;
 *  - os campos de conteúdo são descobertos na importação e guardados em
 *    `fields`; os valores atuais ficam em `content`;
 *  - a saída de e-mail não é suportada (HTML livre não sobrevive às caixas de
 *    e-mail); web e PDF são.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (Studio, HTML livre editável).
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** HTML livre serve para tela e impressão, nunca para e-mail. */
const WEB_E_PDF: StudioOutput[] = ['web', 'pdf'];

registerBlocks([
  {
    key: 'section.html',
    label: 'Seção em HTML livre',
    family: 'layout',
    icon: 'code',
    outputs: WEB_E_PDF,
    props: [
      {
        key: 'title',
        label: 'Nome da seção',
        type: 'text',
        defaultValue: 'Seção',
        help: 'Só aparece no editor, para você reconhecer a faixa.',
      },
      {
        key: 'bleed',
        label: 'Largura',
        type: 'select',
        defaultValue: 'full',
        options: [
          { value: 'full', label: 'Ocupa toda a largura' },
          { value: 'contained', label: 'Com margem lateral' },
        ],
      },
      // ------------------------------------------------------------ Avançado
      {
        key: 'html',
        label: 'Código HTML da seção',
        type: 'richtext',
        advanced: true,
        help: 'O código é limpo pelo servidor ao salvar e ao publicar.',
      },
      {
        key: 'css',
        label: 'Estilos da seção',
        type: 'richtext',
        advanced: true,
        help: 'Os estilos valem apenas dentro desta seção.',
      },
      {
        key: 'fields',
        label: 'Campos de conteúdo',
        type: 'group',
        advanced: true,
        help: 'Descobertos na importação; editados no início do painel.',
      },
      {
        key: 'content',
        label: 'Valores do conteúdo',
        type: 'group',
        advanced: true,
      },
    ],
  },
]);
